import { useEffect, useState } from "react";
import api, { rupees } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, CreditCard, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Billing() {
    const { user } = useAuth();
    const [packages, setPackages] = useState([]);
    const [me, setMe] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/billing/packages"), api.get("/billing/me")])
            .then(([p, m]) => { setPackages(p.data); setMe(m.data); });
    }, []);

    const buy = async (pkg) => {
        setBusyId(pkg.id);
        try {
            const { data } = await api.post("/billing/checkout", {
                package_id: pkg.id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create checkout");
            setBusyId(null);
        }
    };

    if (!packages.length) return <LoadingBlock />;

    return (
        <div data-testid="billing-page">
            <PageHeader
                eyebrow="Billing"
                title="Power your advisory practice."
                subtitle="LifeVault is free for families. Advisors choose a plan to manage clients at scale."
                testid="billing-header"
            />
            <div className="px-8 py-8 max-w-5xl space-y-10">
                {/* Current plan */}
                {me?.active_plan ? (
                    <section className="rounded-2xl border border-primary bg-primary/10 p-6" data-testid="current-plan">
                        <div className="flex items-center gap-3 mb-2">
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                            <span className="label-tag text-primary">Active plan</span>
                        </div>
                        <div className="font-display text-2xl font-bold">
                            {packages.find((p) => p.id === me.active_plan)?.name || me.active_plan}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                            Last paid: {me.last_payment?.created_at?.slice(0, 10)}
                        </div>
                    </section>
                ) : null}

                {(user?.role !== "advisor" && user?.role !== "admin") ? (
                    <div className="rounded-lg border border-dashed border-border p-8 text-sm text-muted-foreground">
                        Billing plans are for <strong>advisor accounts</strong>. Family use of LifeVault is free.
                    </div>
                ) : (
                    <section className="grid md:grid-cols-2 gap-6">
                        {packages.map((p) => {
                            const featured = p.id === "advisor_pro";
                            return (
                                <article
                                    key={p.id}
                                    data-testid={`pkg-${p.id}`}
                                    className={`relative rounded-2xl p-8 transition-colors duration-200 ${
                                        featured
                                            ? "border-primary bg-gradient-to-br from-primary to-primary/80 text-primary-foreground border"
                                            : "border border-border bg-card"
                                    }`}
                                >
                                    {featured ? (
                                        <Sparkles className="absolute right-6 top-6 h-5 w-5 opacity-80" />
                                    ) : null}
                                    <div className={`text-xs uppercase tracking-[0.28em] font-semibold ${featured ? "opacity-90" : "text-muted-foreground"}`}>
                                        {p.name}
                                    </div>
                                    <div className="mt-3 font-display text-5xl font-bold font-mono">
                                        {rupees(p.amount)}
                                    </div>
                                    <div className={`mt-2 text-sm ${featured ? "opacity-90" : "text-muted-foreground"}`}>
                                        {p.tagline}
                                    </div>
                                    <p className={`mt-4 text-sm ${featured ? "opacity-95" : ""}`}>
                                        {p.description}
                                    </p>
                                    <Button
                                        onClick={() => buy(p)}
                                        disabled={busyId === p.id}
                                        data-testid={`pkg-buy-${p.id}`}
                                        className={`mt-6 rounded-full ${
                                            featured ? "bg-background text-foreground hover:bg-background/90" : ""
                                        }`}
                                    >
                                        <CreditCard className="mr-2 h-4 w-4" />
                                        {busyId === p.id ? "Redirecting…" : "Subscribe"} <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </article>
                            );
                        })}
                    </section>
                )}

                <p className="text-xs text-muted-foreground">
                    Test mode is enabled — use Stripe's test card <code className="font-mono">4242 4242 4242 4242</code>,
                    any future date, any CVC, any ZIP.
                </p>
            </div>
        </div>
    );
}
